import React from 'react'
import { useRouter } from 'next/router'
import NextLink from 'next/link'

import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Spinner } from '@wartek-id/spinner'

import { Header } from 'app/KinerjaShared/components/Header'
import { SimpleCard } from 'app/KinerjaPSPegawaiPage/components/SimpleCard'
import { DialogMulaiPelaksanaanConfirmation } from 'app/KinerjaPenilikPegawaiPage/components/DialogMulaiPelaksanaanConfirmation'
import PlanningCard from 'app/KinerjaPenilikPegawaiPage/pages/Perencanaan/Beranda/PlanningCard'
import { PENGELOLAAN_KINERJA_PENILIK_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import {
  usePerencanaanPenilikSummary,
  usePenilikStatus,
} from 'app/KinerjaPenilikPegawaiPage/hooks/useSummary'
import {
  usePerilakuKerjaStore,
  usePraktikKinerjaDraftStore,
  usePengembanganKompetensiDraftStore,
} from 'app/KinerjaPenilikPegawaiPage/stores/kinerja-penilik-pegawai'
import { useStoreByUserId } from 'app/KinerjaPenilikPegawaiPage/hooks/useStoreByUserId'
import { DialogPelaksanaanTugas } from 'app/KinerjaPenilikPegawaiPage/components/DialogPelaksanaanTugas'
import { usePenilikTrackEvent } from 'app/KinerjaPenilikPegawaiPage/hooks/usePenilikTrackEvent'

export function PenilikPerencanaanBerandaPage() {
  const [openDialog, setOpenDialog] = React.useState<
    'mulai-pelaksanaan' | 'pelaksanaan-tugas' | null
  >(null)
  const router = useRouter()

  // set Store by User ID
  useStoreByUserId()

  const { trackEvent } = usePenilikTrackEvent()

  const { perencanaan, apiStatus } = usePerencanaanPenilikSummary()
  const { summary, summaryFetchStatus, isPelaksanaanState } = usePenilikStatus()
  const { hasDraft: hasPraktikKinerjaDraft } = usePraktikKinerjaDraftStore(
    (state) => ({
      hasDraft: state.hasDraft,
    })
  )
  const { hasDraft: hasPerilakuKerjaDraft } = usePerilakuKerjaStore(
    (state) => ({
      hasDraft: state.hasDraft,
    })
  )
  const { hasDraft: hasPengembanganKompetensiDraft } =
    usePengembanganKompetensiDraftStore((state) => ({
      hasDraft: state.hasDraft,
    }))

  const isAllReady = summary?.perencanaanStatus === 'SIAP_DIAJUKAN'
  const isAllWaitingForApproval =
    summary?.perencanaanStatus === 'SEDANG_DIPERIKSA' ||
    summary?.perencanaanStatus === 'DIKIRIM_TK'
  const isAllApproved = ['DISETUJUI_PPK', 'PELAKSANAAN_DIMULAI'].includes(
    summary?.perencanaanStatus
  )
  const isNeedRevisionByTK =
    summary?.perencanaanStatus === 'DIARAHKAN_REVISI_TK'
  const isNeedRevisionByPPK =
    summary?.perencanaanStatus === 'DIARAHKAN_REVISI_PPK'
  const isNeedRevision = isNeedRevisionByTK || isNeedRevisionByPPK
  const isRangkumanEnabled = isAllWaitingForApproval || isAllApproved

  const handleBack = () => {
    router.push(PENGELOLAAN_KINERJA_PENILIK_PEGAWAI_PATH)
  }

  React.useEffect(() => {
    if (isPelaksanaanState) {
      router.replace(PENGELOLAAN_KINERJA_PENILIK_PEGAWAI_PATH)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPelaksanaanState])

  if (
    summaryFetchStatus === 'loading' ||
    apiStatus === 'loading' ||
    isPelaksanaanState
  ) {
    return (
      <div data-testid="loading" className="flex justify-center w-full pt-16">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="w-full">
      <div className="p-8 bg-white">
        <Header
          title="Perencanaan Kinerja"
          onBack={handleBack}
          rightNode={
            <div className="flex gap-4 items-center w-[334px]">
              {(isAllReady || isNeedRevision) && (
                <Button
                  size="md"
                  className="ml-auto"
                  onClick={() => {
                    router.push(
                      `${PENGELOLAAN_KINERJA_PENILIK_PEGAWAI_PATH}/perencanaan/ajukan`
                    )
                  }}
                >
                  Ajukan Perencanaan
                </Button>
              )}
            </div>
          }
        />
      </div>

      <div className="flex justify-center gap-5 mt-10">
        <SimpleCard className="p-5 flex flex-col gap-6 max-w-[344px] h-fit">
          <Text variant="heading-sm">Tahapan Perencanaan Kinerja</Text>

          <ol className="flex flex-col gap-4 ml-5 list-decimal">
            <Text as="li">
              Isi Perencanaan lalu ajukan setelah semua sudah terisi
            </Text>
            <Text as="li">
              Pemeriksaan oleh Tim Kinerja dan Pejabat Penilai Kinerja
              <Text
                as="span"
                variant="body-sm"
                color="subdued"
                className="block mt-1"
              >
                Lakukan revisi jika ada arahan
              </Text>
            </Text>
            <Text as="li">
              Persetujuan Perencanaan oleh Pejabat Penilai Kinerja
            </Text>
            <Text as="li">Mulai tahap Pelaksanaan</Text>
          </ol>

          {/** TODO: set later */}
          <Button
            as="a"
            variant="ghost"
            size="lg"
            href={'#'}
            target="_blank"
            rel="noreferrer"
            className="space-x-1"
          >
            <Icon fontSize="sm" color="informational">
              help
            </Icon>
            <span>Tentang Perencanaan Kinerja</span>
          </Button>
        </SimpleCard>

        <div className="flex flex-col gap-5">
          {apiStatus === 'success' && isNeedRevision && (
            <SimpleCard
              data-testid="card-need-revision"
              className="flex flex-col gap-2 p-5 w-[632px]"
            >
              <Text variant="heading-sm">
                Lakukan revisi sesuai arahan dari{' '}
                {isNeedRevisionByTK ? 'Tim Kinerja' : 'Pejabat Penilai Kinerja'}
                :
              </Text>

              <Text>{perencanaan?.catatanRevisiPerencanaan}</Text>

              <div className="flex items-center gap-3">
                <Icon color="critical">info</Icon>

                <Text color="critical">Anda perlu ajukan ulang.</Text>
              </div>
            </SimpleCard>
          )}
          {apiStatus === 'success' && isAllWaitingForApproval && (
            <SimpleCard
              data-testid="card-waiting-approval"
              className="flex flex-col gap-2 p-5 w-[632px] !bg-[#FFF2DB] !border-[#FFC453] !rounded-md"
            >
              <Text variant="body-sm">
                Perencanaan sedang diperiksa oleh Tim Kinerja dan Pejabat
                Penilai Kinerja
              </Text>
            </SimpleCard>
          )}
          <SimpleCard className="flex flex-col gap-2 p-3 w-[632px] h-fit">
            {apiStatus === 'success' && (
              <>
                <PlanningCard
                  data-testid="praktik-kinerja-card"
                  imageSrc="/illustrations/img-interactive-activity.png"
                  title="Praktik Kinerja"
                  isDraft={hasPraktikKinerjaDraft()}
                  state={perencanaan.praktikKinerjaStatus}
                  href={`${PENGELOLAAN_KINERJA_PENILIK_PEGAWAI_PATH}/perencanaan/praktik-kinerja`}
                  onCtaClick={() => {
                    trackEvent(
                      'perencanaan pegawai',
                      'create draft perencanaan',
                      {
                        aspect: 'praktik kinerja',
                      }
                    )
                  }}
                />
                <PlanningCard
                  imageSrc="/illustrations/img-reflection-documents-magnifying-glass.png"
                  title="Perilaku Kerja"
                  isDraft={hasPerilakuKerjaDraft()}
                  state={perencanaan.perilakuKerjaStatus}
                  href={`${PENGELOLAAN_KINERJA_PENILIK_PEGAWAI_PATH}/perencanaan/perilaku-kerja`}
                  onCtaClick={() => {
                    trackEvent(
                      'perencanaan pegawai',
                      'create draft perencanaan',
                      {
                        aspect: 'perilaku kerja',
                      }
                    )
                  }}
                />
                <PlanningCard
                  imageSrc="/illustrations/img-flow-process.png"
                  title="Pengembangan Kompetensi"
                  isDraft={hasPengembanganKompetensiDraft()}
                  state={perencanaan.pengembanganKompetensiStatus}
                  href={`${PENGELOLAAN_KINERJA_PENILIK_PEGAWAI_PATH}/perencanaan/pengembangan-kompetensi`}
                  onCtaClick={() => {
                    trackEvent(
                      'perencanaan pegawai',
                      'create draft perencanaan',
                      {
                        aspect: 'pengembangan kompetensi',
                      }
                    )
                  }}
                />
                <PlanningCard
                  imageSrc="/illustrations/img-book-rapor-graphic-doc.png"
                  title="Pelaksanaan Tugas"
                >
                  <Button
                    size="md"
                    color="white"
                    onClick={() => setOpenDialog('pelaksanaan-tugas')}
                  >
                    Cek
                  </Button>
                </PlanningCard>
              </>
            )}
          </SimpleCard>

          {isRangkumanEnabled && (
            <SimpleCard className="px-9 py-6 w-[632px] h-fit">
              <div className="flex items-center justify-between flex-1 gap-1">
                <div className="flex flex-col gap-3">
                  <Text variant="heading-sm">Rangkuman Perencanaan</Text>
                </div>

                <div className="ml-auto">
                  <NextLink
                    href={`${PENGELOLAAN_KINERJA_PENILIK_PEGAWAI_PATH}/perencanaan/rangkuman`}
                  >
                    <Button size="md" color="white" className="w-32">
                      Cek
                    </Button>
                  </NextLink>
                </div>
              </div>
            </SimpleCard>
          )}
        </div>
      </div>

      <DialogPelaksanaanTugas
        isOpen={openDialog === 'pelaksanaan-tugas'}
        onClose={() => setOpenDialog(null)}
      />

      <DialogMulaiPelaksanaanConfirmation
        isOpen={openDialog === 'mulai-pelaksanaan'}
        onClose={() => setOpenDialog(null)}
      />
    </div>
  )
}

export default PenilikPerencanaanBerandaPage
